/**
 * FilterBuilder Component
 * 
 * Main filter UI that allows users to add/remove multiple filter conditions.
 * Integrates with useContactsTable and useDealsTable hooks.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Button,
  Text,
  IconButton,
  Collapse,
  useDisclosure,
  Badge,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react';
import { FiFilter, FiPlus, FiChevronDown, FiChevronUp, FiTrash2 } from 'react-icons/fi';
import FilterCondition from './FilterCondition';

/**
 * FilterBuilder - Main component for building filter configurations
 * 
 * @param {Array} filters - Current filter conditions
 * @param {Function} onFiltersChange - Callback when filters change
 * @param {Array} fieldDefinitions - Available fields to filter on
 * @param {string} entityType - 'contacts' or 'deals'
 * @param {boolean} defaultOpen - Force the panel to be open (controlled externally)
 * @param {Array} contactTypes - Admin-configured contact types from CRM settings
 * @param {Array} contactTags - Admin-configured contact tags from Supabase
 * @param {Array} dealTags - Admin-configured deal tags from Supabase
 * @param {Array} dealStages - Admin-configured deal stages from CRM settings
 * @param {Array} campaignOptions - Active campaign templates ({ value, label, activeCount }) from useCampaignFilterOptions
 */
export default function FilterBuilder({
  filters = [],
  onFiltersChange,
  fieldDefinitions = [],
  entityType = 'contacts',
  defaultOpen = true, // Default to open when shown
  contactTypes = [],
  contactTags = [],
  dealTags = [],
  dealStages = [],
  campaignOptions = [],
}) {
  const { isOpen, onToggle, onOpen } = useDisclosure({ defaultIsOpen: defaultOpen || filters.length > 0 });
  const [localFilters, setLocalFilters] = useState(filters);
  
  // When defaultOpen changes to true, open the panel
  useEffect(() => {
    if (defaultOpen) {
      onOpen();
    }
  }, [defaultOpen, onOpen]);
  
  // Sync local filters when external filters change
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);
  
  const panelBg = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  // Update local filters and propagate to parent
  const updateFilters = (newFilters) => {
    setLocalFilters(newFilters);
  };
  
  // Apply filters to parent
  const handleApply = () => {
    onFiltersChange(localFilters);
  };
  
  // Add a new empty filter condition
  const handleAddFilter = () => {
    const newFilter = {
      id: Date.now().toString(),
      field: '',
      operator: '',
      value: '',
    };
    updateFilters([...localFilters, newFilter]);
  };
  
  // Update a specific filter
  const handleUpdateFilter = (index, updatedFilter) => {
    const newFilters = [...localFilters];
    newFilters[index] = updatedFilter;
    updateFilters(newFilters);
  };
  
  // Remove a filter
  const handleRemoveFilter = (index) => {
    const newFilters = localFilters.filter((_, i) => i !== index);
    updateFilters(newFilters);
  };
  
  // Clear all filters
  const handleClearAll = () => {
    updateFilters([]);
    onFiltersChange([]);
  };
  
  // Count active (complete) filters
  const activeFilterCount = localFilters.filter(f => f.field && f.operator).length;
  
  return (
    <Box>
      {/* Toggle Header */}
      <HStack
        justify="space-between"
        p={2}
        cursor="pointer"
        onClick={onToggle}
        borderRadius="md"
        _hover={{ bg: useColorModeValue('gray.100', 'gray.700') }}
      >
        <HStack spacing={2}>
          <FiFilter />
          <Text fontWeight="medium" fontSize="sm">Filters</Text>
          {activeFilterCount > 0 && (
            <Badge colorScheme="blue" fontSize="xs">
              {activeFilterCount} active
            </Badge>
          )}
        </HStack>
        <IconButton
          icon={isOpen ? <FiChevronUp /> : <FiChevronDown />}
          size="xs"
          variant="ghost"
          aria-label="Toggle filters"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        />
      </HStack>
      
      {/* Filter Panel */}
      <Collapse in={isOpen}>
        <Box
          p={4}
          bg={panelBg}
          borderRadius="md"
          borderWidth="1px"
          borderColor={borderColor}
          mt={2}
        >
          <VStack align="stretch" spacing={3}>
            {/* Filter Conditions */}
            {localFilters.length === 0 ? (
              <Text fontSize="sm" color="gray.500" textAlign="center" py={2}>
                No filters applied. Click "Add Filter" to get started.
              </Text>
            ) : (
              localFilters.map((filter, index) => (
                <FilterCondition
                  key={filter.id || index}
                  filter={filter}
                  fieldDefinitions={fieldDefinitions}
                  entityType={entityType}
                  contactTypes={contactTypes}
                  contactTags={contactTags}
                  dealTags={dealTags}
                  dealStages={dealStages}
                  campaignOptions={campaignOptions}
                  onChange={(updated) => handleUpdateFilter(index, updated)}
                  onRemove={() => handleRemoveFilter(index)}
                />
              ))
            )}
            
            {/* Add Filter Button */}
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<FiPlus />}
              onClick={handleAddFilter}
              alignSelf="flex-start"
            >
              Add Filter
            </Button>
            
            <Divider />
            
            {/* Action Buttons */}
            <HStack justify="space-between">
              <Button
                size="sm"
                variant="ghost"
                colorScheme="red"
                leftIcon={<FiTrash2 />}
                onClick={handleClearAll}
                isDisabled={localFilters.length === 0}
              >
                Clear All
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                onClick={handleApply}
                isDisabled={localFilters.length === 0}
              >
                Apply Filters
              </Button>
            </HStack>
          </VStack>
        </Box>
      </Collapse>
    </Box>
  );
}
